import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { ContentPlaceholder } from '../../../../../../components/content-placeholder/content-placeholder';
import { useStrategyStore, useThroughputTabStore } from '../../../../../../store/strategy-store/strategy-store';
import { showRelatedTasks } from '../../../../../../store/strategy-store/strategy-store.actions';
import { ThroughputType } from '../../../../../../store/strategy-store/strategy-store.type';
import { graphCambridgeBlue, secondaryBase, skyDark, skyLight } from '../../../../../../styles/design-tokens';
import { getMetricLabel } from '../../../../strategy.helpers';
import { LineChartDot } from '../../../components/line-chart/line-chart-dot';
import { hasArrayData } from '../../../initiative-performance.helpers';
import { InitiativeProgressChartLegend } from './initiative-progress-chart-legend';
import { InitiativeProgressChartTooltip } from './initiative-progress-chart-tooltip';
import { getChartLineName, getCurrentMonthLabel } from './initiative-progress-chart.helpers';
import { useChartData } from './initiative-progress-chart.hooks';
import { DataKey, type ChartDataItem } from './initiative-progress-chart.type';

export const InitiativeProgressChart = forwardRef<HTMLDivElement>((_, ref) => {
  const i18n = useTranslation();
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const metric = useThroughputTabStore((state) => state.metric);
  const visibleThroughputTypes = useThroughputTabStore((state) => state.visibleThroughputTypes);

  const chartData = useChartData(metric);
  const hasData = hasArrayData(chartData);

  const currentMonthLabel = useMemo(() => getCurrentMonthLabel(), []);

  const handleChartPointClick = (throughputType: 'total' | 'completed', dotProps: unknown) => {
    try {
      const { payload } = dotProps as { payload?: ChartDataItem };

      if (!payload || !payload.rawDate) {
        console.error('Invalid payload structure in chart click event');
        return;
      }

      showRelatedTasks({ throughput: throughputType, date: dayjs(payload.rawDate, 'YYYY-MM').toDate() });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  if (!initiative || !hasData) {
    return <ContentPlaceholder />;
  }

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={160}>
        <LineChart data={chartData} margin={{ top: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            label={{
              value: getMetricLabel(metric, i18n.t),
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              offset: 10,
            }}
          />
          <RechartsTooltip content={<InitiativeProgressChartTooltip />} />
          <ReferenceLine x={currentMonthLabel} stroke={skyDark} yAxisId="left" />

          {/* Historical Data - Solid Lines */}
          {visibleThroughputTypes[ThroughputType.Total] && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="totalRegular"
              name={getChartLineName(DataKey.TotalRegular, metric, i18n.t)}
              stroke={secondaryBase}
              strokeWidth={1}
              dot={<LineChartDot color={secondaryBase} />}
              activeDot={
                <LineChartDot color={secondaryBase} onClick={(dotProps) => handleChartPointClick('total', dotProps)} />
              }
              isAnimationActive={false}
              connectNulls={true}
            />
          )}
          {visibleThroughputTypes[ThroughputType.Completed] && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="completedRegular"
              name={getChartLineName(DataKey.CompletedRegular, metric, i18n.t)}
              stroke={graphCambridgeBlue}
              strokeWidth={1}
              dot={<LineChartDot color={graphCambridgeBlue} />}
              activeDot={
                <LineChartDot
                  color={graphCambridgeBlue}
                  onClick={(dotProps) => handleChartPointClick('completed', dotProps)}
                />
              }
              isAnimationActive={false}
              connectNulls={true}
            />
          )}

          {/* Future Data - Dashed Lines */}
          {visibleThroughputTypes[ThroughputType.Total] && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="totalFuture"
              name={getChartLineName(DataKey.TotalFuture, metric, i18n.t)}
              stroke={secondaryBase}
              strokeWidth={1}
              strokeDasharray="5 5"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              connectNulls={true}
            />
          )}
          {visibleThroughputTypes[ThroughputType.Completed] && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="completedFuture"
              name={getChartLineName(DataKey.CompletedFuture, metric, i18n.t)}
              stroke={graphCambridgeBlue}
              strokeWidth={1}
              strokeDasharray="5 5"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              connectNulls={true}
            />
          )}

          <Legend content={<InitiativeProgressChartLegend />} verticalAlign="bottom" height={36} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});
